import React, { useEffect, useState } from "react";
import Image from "src/components/common/Image";
import SEO from "src/components/common/SEO";
import { STORE_LINKS } from "src/constants/common";
import environment from "src/constants/environment";
import analytics from "src/helpers/analytics";
import { EVENT_NAMES, SCREEN_NAMES } from "src/helpers/analytics/events";
import auth from "src/helpers/auth";
import { getUrlParams } from "src/helpers/common";

const AppDownload: React.FC = () => {
  const [isDesktop, setIsDesktop] = useState(false);
  const queryParams = getUrlParams() as Record<string, string>;
  const appName =
    ((queryParams as any)?.appName as "GOODFLIP_APP" | "GOODFLIP_CARE_APP") ||
    "GOODFLIP_CARE_APP";
  const appLinks = STORE_LINKS[appName as keyof typeof STORE_LINKS];

  // Detect device
  useEffect(() => {
    // Event: APP_DOWNLOAD_SCREEN
    analytics.pageView(SCREEN_NAMES.APP_DOWNLOAD_SCREEN);

    const userAgent =
      navigator.userAgent || navigator.vendor || (window as any).opera;

    const isAndroid = /android/i.test(userAgent);
    const isIOS = /iPad|iPhone|iPod/.test(userAgent);

    if (isAndroid) {
      // Event: DOWNLOAD_FOR_ANDROID_REDIRECTED
      analytics.trackEvent(EVENT_NAMES.DOWNLOAD_FOR_ANDROID_REDIRECTED, {
        screen_name: SCREEN_NAMES.APP_DOWNLOAD_SCREEN,
        link: appLinks.PLAY_STORE,
      });
      window.location.href = appLinks.PLAY_STORE;
    } else if (isIOS) {
      // Event: DOWNLOAD_FOR_IOS_REDIRECTED
      analytics.trackEvent(EVENT_NAMES.DOWNLOAD_FOR_IOS_REDIRECTED, {
        screen_name: SCREEN_NAMES.APP_DOWNLOAD_SCREEN,
        link: appLinks.APP_STORE,
      });
      window.location.href = appLinks.APP_STORE;
    } else {
      setIsDesktop(true);
    }

    // Cleanup function - runs when component unmounts (user leaves the screen)
    return () => {
      (async () => {
        await auth.handleLogout();
      })();
    };
  }, []);

  if (!isDesktop) return null; // returning null until desktop layout is shown

  const title =
    appName === "GOODFLIP_APP"
      ? "Download GoodFlip App"
      : "Download GoodFlip Care App";

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-5">
      <SEO
        title={title}
        description="Download the GoodFlip Care app to get personalised health support, doctor guidance and monitoring via GoodFlip Care programs."
        keywords="GoodFlip Care, Personalised Support Programs, Health Support, Doctor Guidance, Monitoring"
      />
      <div className="bg-white shadow-md rounded-2xl p-8 max-w-md w-full text-center space-y-6">
        <h1 className="text-2xl font-semibold text-gray-800">{title}</h1>

        <p className="text-gray-600">
          Scan the QR code or choose your platform below.
        </p>

        {/* QR Code */}
        <div className="flex items-center justify-center px-5 md:px-10 lg:px-20">
          <Image
            src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(
              `${environment.DOMAIN_URL}/app-links/app-download`
            )}`}
            alt="App QR Code"
            width={60}
            height={60}
            loading="lazy"
          />
        </div>

        {/* Buttons */}
        <div className="space-y-3 mt-4">
          <a
            href={appLinks.PLAY_STORE}
            className="block w-full bg-green-600 text-white py-3 rounded-lg font-medium hover:bg-green-700 transition"
            onClick={() => {
              // Event: DOWNLOAD_FOR_ANDROID_CLICKED
              analytics.trackEvent(EVENT_NAMES.DOWNLOAD_FOR_ANDROID_CLICKED, {
                screen_name: SCREEN_NAMES.APP_DOWNLOAD_SCREEN,
              });
            }}
          >
            Download for Android
          </a>

          <a
            href={appLinks.APP_STORE}
            className="block w-full bg-black text-white py-3 rounded-lg font-medium hover:bg-gray-900 transition"
            onClick={() => {
              // Event: DOWNLOAD_FOR_IOS_CLICKED
              analytics.trackEvent(EVENT_NAMES.DOWNLOAD_FOR_IOS_CLICKED, {
                screen_name: SCREEN_NAMES.APP_DOWNLOAD_SCREEN,
              });
            }}
          >
            Download for iOS
          </a>
        </div>

        <p className="text-sm text-gray-500 mt-4">
          If you're on a desktop, use the QR code to open the store link.
        </p>
      </div>
    </div>
  );
};

export default AppDownload;
